import React, { FC, useContext, useEffect, useRef, useState, Suspense } from 'react';
import { LocaleUtils } from 'react-day-picker';
import moment from 'moment';
import { StyledAddDatesDiv, StyledDatesFooter } from './addDates.styles';
import { ViewportContext, FormValuesContext, FocusContext, ContentContext } from '../../index.contexts';
import { AddDatesProps, FocusedInputType } from './addDates.types';
import Popup from '../../atoms/popup/popup';
import PopupHeader from '../../atoms/popupHeader/popupHeader';
import PopupMain from '../../atoms/popupMain/popupMain';
import { DATES } from '../../../constants/StoreConstants';
import {
  DateObjectType,
  getNextDateObject,
  getPrevDateObject,
  checkLocaleToAddYears,
  modifyDateStringForThai,
  convertArabicToEnglishNumbers,
} from '../../../utils/DateUtils';
import DatesHeader from '../../atoms/DatesHeader/datesHeader';
import FlexibleDate from '../../atoms/flexibleDate/flexibleDate';
import { setDates } from '../../../store/store.actions';
import { MOMENT_CN_LANG, getLocalizedDate as getLocalDate } from '@marriott/mi-ui-library';
import { isCNLocale, isTCLocale } from '@marriott/shared/mi-helper-utils';
import {
  CALENDAR_MAX_ALLOWED_DAYS,
  DATE_FORMAT_DEFAULT,
  VARIATION_DESKTOP,
  VARIATION_TABLET,
  IS_LOCALE_THAI,
} from '../../../constants/ApplicationConstants';
import PopupFooter from '../../atoms/popupFooter/popupFooter';
import CommonUtils from '../../../utils/CommonUtils';
import ErrorBoundary from '../../atoms/errorBoundary';
import ShopPageDatesHeaderBox from '../../atoms/ShopPageDatesHeader/shopPageDatesHeader';
import { START_DATE, END_DATE } from '../../index.constants';
import FlexibleDateCalendar from '../../atoms/flexibleDateCalendar/flexibleDateCalendar';
import ToggleSwitch from '../../atoms/toggleSwitch/toggleSwitch';
import ShopPageSingleDateHeaderBox from '../../atoms/ShopPageDatesHeader/shopPageSingleDateHeader';
import SingleDateHeader from '../../atoms/DatesHeader/singleDateHeader';
import { renderedData } from '../../SearchFormComponent';
import { usePageContext } from '../../../../../../context/PageContext';
import { JP_LOCALE, AR_LOCALE, formattingRequiredLocales } from '../../../../../../modules/constants';
import { getCustomFormattedDate } from '../../../../../../modules/utils';
import { DEFAULT_SINGLE_DATE_LIMIT } from '../../../../../../constants/Calendar.constant';

const Calendar = React.lazy(() => import('@marriott/mi-ui-library-shop'));

const AddDates: FC<AddDatesProps> = ({
  onCancel,
  onChange: changeMobileState,
  isHideFlexibleDates,
  isSearchPage = false,
  enableTwoSeparateButtonsForDates = false,
  focusedInput = START_DATE,
  isADFPage = false,
}) => {
  const clickTrackingLoc = 'Search Form';
  /** Contexts */
  const isMobileView = useContext(ViewportContext);
  const { formValues, setFormValues, setIsPopupOpen } = useContext(FormValuesContext);
  const { focusComp, setIsUserInteractWithComp, setIsUserEditSearchForm } = useContext(FocusContext);
  const { currentLocale } = usePageContext();
  const {
    firstDayOfWeek,
    dateFormat,
    alternateDateFormat,
    enableFlexibleSearch,
    maxNumberOfNights,
    checkInLabel,
    checkOutLabel,
    exactDatesTabText,
    flexibleTabText,
    reset,
    stayDates,
    nights,
    night,
    flexible: flexibleLabelText,
    weekdays,
    weekdaysLong,
    months,
    cancel,
    done,
    clear,
    weekdaysShort,
    monthsShort,
    yearLabel,
    yearSearchLabel,
  } = useContext(ContentContext);
  const {
    nightCount,
    setNightCount,
    yearSelected,
    setYearSelected,
    monthSelected,
    setMonthSelected,
    tabSelected,
    setTabSelected,
  } = useContext(renderedData);
  /** States */
  const [popupOpenState, setPopupOpenState] = useState(false);
  const [focusedState, setFocusedState] = useState<FocusedInputType>(focusedInput);

  const compRef = useRef<HTMLDivElement>(null);
  const { fromDate, toDate, flexible } = formValues?.[DATES] ?? {};
  const numberOfNights = CommonUtils.getNumberOfNights(fromDate, toDate);
  const [count, setCount] = useState(0);
  const [disableHover, setDisableHover] = useState(false);
  const [showIcon, setShowIcon] = useState(false);
  const fromSearchForm = true;
  const tab1Label = exactDatesTabText;
  const tab2Label = flexibleTabText;
  const sessionData = window?.sessionData;
  const useEnglishMomentForCalculation = currentLocale === AR_LOCALE ? true : false;
  let singleLimit = sessionData?.data
    ? sessionData?.data?.AriesCommon?.singleDateLimit
    : sessionData?.cacheData?.data?.AriesCommon?.singleDateLimit;
  if (!singleLimit) {
    if (useEnglishMomentForCalculation) {
      singleLimit = moment().locale('en').add(DEFAULT_SINGLE_DATE_LIMIT, 'd');
    } else {
      singleLimit = moment().locale('en').add(DEFAULT_SINGLE_DATE_LIMIT, 'd');
    }
  }
  let singleDateLimit = new Date(singleLimit ? moment(singleLimit).format() : '').setDate(
    new Date(singleLimit ? moment(singleLimit).format() : '').getDate() - 1
  );

  if (useEnglishMomentForCalculation) {
    singleDateLimit = new Date(singleLimit ? moment(singleLimit).locale('en').format() : '').setDate(
      new Date(singleLimit ? moment(singleLimit).locale('en').format() : '').getDate() - 1
    );
  }

  // based on numberOfNights, set label  to 'Dates' or '1 Night' or '2 Nights'
  let labelText = stayDates;
  if (numberOfNights > 0) {
    labelText = `${numberOfNights} ${numberOfNights > 1 ? nights : night}${flexible ? ` (${flexibleLabelText})` : ''}`;
  }

  // to get the dates based on the localization
  const getLocalizedDate = (date: DateObjectType | undefined, alternativeFormat?: string): string => {
    moment.locale('locale');
    let localizedDate = '';

    if (date) {
      if (currentLocale === JP_LOCALE || currentLocale === AR_LOCALE) {
        moment.locale(currentLocale.slice(0, 2));
      } else {
        moment.locale(currentLocale);
      }

      const formattedDate = moment(date).format();
      // const formattedDate = moment.utc(date).format();  // for offset date issue
      // const formattedDate = moment.parseZone(date).format();

      if (isTCLocale(currentLocale)) {
        require('moment/locale/zh-tw');
        return moment(formattedDate).locale(currentLocale.toLowerCase()).format(dateFormat);
      } else if (isCNLocale(currentLocale)) {
        try {
          require('moment/locale/zh-cn');
          moment.locale(MOMENT_CN_LANG);
        } catch (e) {
          console.warn('Failed to load locale - ', MOMENT_CN_LANG);
        }
        localizedDate = moment(formattedDate).format(dateFormat);
      } else {
        if (formattingRequiredLocales.includes(currentLocale?.replace('_', '-'))) {
          localizedDate = getCustomFormattedDate(
            getLocalDate(
              date,
              alternativeFormat !== '' && alternativeFormat !== undefined && alternativeFormat !== 'NA'
                ? alternativeFormat
                : (dateFormat as string) ?? DATE_FORMAT_DEFAULT,
              currentLocale
            ),
            currentLocale
          );
        } else {
          localizedDate = moment(formattedDate).format(
            alternativeFormat !== '' && alternativeFormat !== undefined && alternativeFormat !== 'NA'
              ? alternativeFormat
              : (dateFormat as string) ?? DATE_FORMAT_DEFAULT
          );
        }
      }
    }

    return currentLocale === IS_LOCALE_THAI
      ? modifyDateStringForThai(localizedDate)
      : currentLocale === AR_LOCALE
      ? convertArabicToEnglishNumbers(localizedDate)
      : localizedDate;
  };

  // if from and to dates not available then show 'Check in - Check out' else "fromDate - toDate"
  let displayText = `${checkInLabel} - ${checkOutLabel}`;
  if (fromDate || toDate) {
    displayText = `${getLocalizedDate(fromDate)}${toDate ? ` - ${getLocalizedDate(toDate)}` : ''}`;
  }

  // show flexible date checkbox when both from and to dates selected
  const showFlexible = fromDate && toDate && !isHideFlexibleDates;

  const [variation, setVariation] = React.useState<string>(CommonUtils.getVariation());

  let shortWeekdays = weekdays?.split(',');
  // set default weekdays if i18 weekdays value are invalid
  if (shortWeekdays?.length !== 7) {
    shortWeekdays = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  }

  const weekDayInitial = weekdays?.split(',');

  let weeksdaysShort = weekdaysShort?.split(',');
  // set default weekdays if i18 weekdays value are invalid
  if (weeksdaysShort?.length !== 7) {
    weeksdaysShort = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  }

  let weekdaysLongName = weekdaysLong?.split(',');
  if (weekdaysLongName?.length !== 7) {
    weekdaysLongName = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  }

  let monthsValue = months?.split(',');
  // set default month if i18 months value are invalid
  if (monthsValue?.length !== 12) {
    monthsValue = LocaleUtils.getMonths();
  }

  let monthsShortValue = monthsShort?.split(',');
  // set default month if i18 months value are invalid
  if (monthsShortValue?.length !== 12) {
    monthsShortValue = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  }

  const setLanguageLocalization = (): void => {
    moment.locale(isADFPage ? currentLocale : 'locale', {
      months: monthsValue,
      monthsShort: monthsShortValue,
      weekdays: weekdaysLongName,
      weekdaysShort: weeksdaysShort,
    });
  };

  // Set toDate and fromDate values
  const setToDateAndFromDate = (): void => {
    let newFromDate = fromDate as DateObjectType;
    let newToDate = toDate as DateObjectType;
    if (!fromDate && toDate) {
      //if fromDate is not selected setting the previous day of the toDate as fromDate
      newFromDate = getPrevDateObject(newToDate);
    }
    if (!toDate && newFromDate) {
      //if toDate is not selected setting the next day of the fromDate as toDate
      newToDate = getNextDateObject(newFromDate) ?? null;
    }

    setDateHandler(newFromDate, newToDate);
  };

  const handleResetCalendar = (): void => {
    //Reset Logic to be written
    // handleFlexClear();
    setShowIcon(false);
    setNightCount(1);
    let startOfMonth: moment.Moment;
    if (useEnglishMomentForCalculation) {
      setYearSelected(Number(moment().locale('en').format('YYYY')));
      setMonthSelected(Number(moment().locale('en').format('M')) - 1);
      startOfMonth = moment(Number(moment().locale('en').format('M')), 'M');
    } else {
      setYearSelected(Number(moment().format('YYYY')));
      setMonthSelected(Number(moment().format('M')) - 1);
      startOfMonth = moment(Number(moment().format('M')), 'M');
    }

    startOfMonth = startOfMonth < moment() ? moment() : startOfMonth;
    if (Number(startOfMonth.format('YYYY')) < Number(moment().format('YYYY'))) {
      startOfMonth = moment(startOfMonth).add(365, 'days');
    }
    setFormValues(
      setDates({
        ...(formValues?.[DATES] ?? {}),
        fromDate: startOfMonth,
        toDate: moment(startOfMonth).add(1, 'days'),
      })
    );
    setDateHandler(startOfMonth, moment(startOfMonth).add(1, 'days'));
  };

  // effect to handle the changes after popup state is set to open
  useEffect(() => {
    // to scroll search form to top on interaction
    setIsPopupOpen?.(!!popupOpenState);

    if (popupOpenState) {
      setFocusOnFirstBtn();
      // setFocusedState(START_DATE);
    } else {
      setToDateAndFromDate();
      setLanguageLocalization();
      setIsUserEditSearchForm(true);
    }
  }, [popupOpenState]);

  useEffect(() => {
    // To focus the first interactive element
    if (isMobileView) {
      CommonUtils.focusFirstTabbable(compRef);
    }
  }, []);

  useEffect(() => {
    // focus on the first interactive element in desktopview based on FocusContext API
    if (!isMobileView && focusComp === DATES) {
      CommonUtils.focusFirstTabbable(compRef);
    }
  }, [focusComp]);

  // to show popup on desktop on button click
  const showPopup = (): void => {
    setPopupOpenState(true);
    setIsUserInteractWithComp(true);
  };

  // to hide popup on desktop on button click
  const closePopup = (): void => {
    setPopupOpenState(false);
    compRef.current?.querySelector('button')?.focus();
  };

  // set focus on first button in the popup when popup state is set to open
  const setFocusOnFirstBtn = (): void => {
    const popupElemBtn = compRef.current?.querySelector('.search-dates-popup button') as HTMLElement;
    popupElemBtn?.focus();
  };

  // handler to set fromDate in the formValues
  const setDateHandler = (
    startDate: DateObjectType | null,
    endDate: DateObjectType | null,
    resetEndDateFlag?: boolean
  ): void => {
    const from = startDate ?? undefined;
    let end = endDate ?? undefined;

    // if from and end date are same then remove end date and keep focus on end date
    if (from?.isSame(end)) {
      setFocusedState(END_DATE);
      end = undefined;
    }

    //if both from and end dates are selected and focus is on end date shift the focus to from date
    if (from && end && !from?.isSame(end) && focusedState === END_DATE) {
      setFocusedState(START_DATE);
    }

    let fromAndEndDate = {};
    if (!resetEndDateFlag && !startDate && !endDate) {
      fromAndEndDate = { fromDate: from };
    } else if (resetEndDateFlag && !endDate) {
      fromAndEndDate = { toDate: end };
    } else {
      fromAndEndDate = { fromDate: from, toDate: end };
    }

    setFormValues(
      setDates({
        ...(formValues?.[DATES] ?? {}),
        ...fromAndEndDate,
      })
    );

    if (popupOpenState) {
      setIsUserEditSearchForm(true);
    }
  };

  // clean button handler on check in
  const handleClearCheckIn = (): void => {
    setFocusedState(START_DATE);
    setDateHandler(null, null);
  };

  // clear button handler on checkout
  const handleClearCheckOut = (): void => {
    setFocusedState(END_DATE);
    setDateHandler(null, null, true);
  };

  useEffect(() => {
    if (!formValues?.[DATES]?.fromDate && !formValues?.[DATES]?.toDate && !popupOpenState) {
      setTabSelected(tab2Label);
    }
  }, [popupOpenState]);
  // useEffect(()=>{if(){
  //
  //     }},[])
  // handler for flexible checkbox
  const flexibleChangeHandler = (event: React.ChangeEvent<HTMLInputElement>): void => {
    setFormValues(
      setDates({
        ...(formValues?.[DATES] ?? {}),
        flexible: event.target?.checked ? event.target?.value : undefined,
      })
    );
  };

  // sets flexible false when flexible checkbox is hidden
  if (!showFlexible && flexible && !isSearchPage) {
    setFormValues(
      setDates({
        ...(formValues?.[DATES] ?? {}),
        flexible: undefined,
      })
    );
  }

  const handleBackBtn = (): void => {
    // handleResetCalendar();
    if (isMobileView && onCancel) {
      onCancel();
    }
  };

  useEffect(() => {
    if (tabSelected === tab2Label) {
      //Required to sync exact dates to flexible count
      !isMobileView && setFocusedState(START_DATE);
      setFormValues(
        setDates({
          ...(formValues?.[DATES] ?? {}),
          flexible: 'true',
        })
      );
      if (numberOfNights <= Number(maxNumberOfNights) && numberOfNights >= 1) {
        setNightCount(numberOfNights);
      } else {
        setNightCount(1);
        updateDatesOnFlexiSelection(
          Number(moment(fromDate).format('M')) - 1,
          1,
          Number(moment(fromDate).format('YYYY'))
        );
      }
      if (!isADFPage) {
        if (useEnglishMomentForCalculation) {
          setYearSelected(Number(moment(fromDate).locale('en').format('YYYY')));
          setMonthSelected(Number(moment(fromDate).locale('en').format('M')) - 1);
        } else {
          setYearSelected(Number(moment(fromDate).format('YYYY')));
          setMonthSelected(Number(moment(fromDate).format('M')) - 1);
        }
      }
      // Preventing setMonthSelected to set the date before the form values are not get updated in mobile device for ADF.
      if (isADFPage && !isMobileView) {
        if (useEnglishMomentForCalculation) {
          setYearSelected(Number(moment(fromDate).locale('en').format('YYYY')));
          setMonthSelected(Number(moment(fromDate).locale('en').format('M')) - 1);
        } else {
          setYearSelected(Number(moment(fromDate).format('YYYY')));
          setMonthSelected(Number(moment(fromDate).format('M')) - 1);
        }
      }
    } else {
      /* condition to set toDate if no toDate is available , according to number of nights */
      if (fromDate && !toDate) {
        setDateHandler(fromDate ? fromDate : null, moment(fromDate).add(nightCount, 'days'));
      } else if (fromDate && toDate) {
        /* condition to set toDate if toDate is already available */
        setDateHandler(fromDate ? fromDate : null, toDate ? toDate : null);
      } else if (!fromDate && !toDate) {
        setDateHandler(moment(), moment().add(nightCount, 'days'));
      }

      setFormValues(
        setDates({
          ...(formValues?.[DATES] ?? {}),
          fromDate: fromDate ? fromDate : moment(fromDate) ?? moment(),
          toDate: toDate ? toDate : moment(fromDate).add(nightCount, 'days') ?? moment().add(nightCount, 'days'),
          flexible: 'false',
        })
      );
      const differnceBetweenDates = toDate?.diff(moment(singleDateLimit), 'days') || 0;
      if (differnceBetweenDates && differnceBetweenDates > 0) {
        setDateHandler(moment(singleDateLimit).subtract(differnceBetweenDates, 'days'), moment(singleDateLimit));
      }
    }
  }, [tabSelected]);
  // Utility to set variation based on true value
  const setSuccessVariation = (flag: boolean, variation: string): void => {
    if (flag) {
      setVariation(variation);
    }
  };

  //To update variation based on viewport dimensions change
  useEffect(() => {
    // set viewport change event and return callback function to clear events
    const updateViewportHandler = CommonUtils.addRemoveResizeHandler(setSuccessVariation);

    return (): void => {
      // remove change event listner on component unmount
      updateViewportHandler();
    };
  }, []);

  // on click done in mobile state
  const changeMobileStateOnDone = (): void => {
    setToDateAndFromDate();
    changeMobileState?.();
  };

  const handleCheckinClicked = (): void => {
    setFocusedState(START_DATE);
    setCount(count + 1);
  };
  useEffect(() => {
    if (count > 0 && focusedState === START_DATE && !toDate) {
      setDisableHover(true);
    } else {
      setDisableHover(false);
    }
  }, [count, focusedState]);

  const updateDatesOnFlexiSelection = (monthInd: number, nightCount: number, year: number): void => {
    let startOfMonth = useEnglishMomentForCalculation
      ? moment(monthInd + 1, 'M').locale('en')
      : moment(monthInd + 1, 'M');

    // add 366 days to current date if it is leap year and not month of Jan and Feb
    if (Number(startOfMonth.format('YYYY')) < year && year % 2 === 0 && monthInd !== 0 && monthInd !== 1) {
      startOfMonth = useEnglishMomentForCalculation
        ? moment(startOfMonth).locale('en').add(12, 'months')
        : moment(startOfMonth).add(12, 'months');
    }
    // add 365 days to current date if it is non-leap year
    else if (Number(startOfMonth.format('YYYY')) < year) {
      startOfMonth = useEnglishMomentForCalculation
        ? moment(startOfMonth).locale('en').add(12, 'months')
        : moment(startOfMonth).add(12, 'months');
    }

    if (useEnglishMomentForCalculation) {
      startOfMonth = startOfMonth < moment().locale('en') ? moment().locale('en') : startOfMonth;
    } else {
      startOfMonth = startOfMonth < moment() ? moment() : startOfMonth;
    }

    if (nightCount > 1) {
      setShowIcon(true);
    } else {
      setShowIcon(false);
    }

    setFormValues(
      setDates({
        ...(formValues?.[DATES] ?? {}),
        fromDate: startOfMonth,
        toDate: useEnglishMomentForCalculation
          ? moment(startOfMonth).locale('en').add(nightCount, 'days')
          : moment(startOfMonth).add(nightCount, 'days'),
      })
    );
    setDateHandler(
      startOfMonth,
      useEnglishMomentForCalculation
        ? moment(startOfMonth).locale('en').add(nightCount, 'days')
        : moment(startOfMonth).add(nightCount, 'days')
    );

    !isMobileView && setFocusedState(START_DATE);
    if (popupOpenState) {
      setIsUserEditSearchForm(true);
    }
  };

  const handleTabClick = (selectedTab: string): void => {
    setTabSelected(selectedTab);
    // setInteractionsSelected({ ...interactionsSelected, flexibleSearchType: true });
  };
  const flexibleSearchTab = isSearchPage && tabSelected === tab2Label;
  return (
    <StyledAddDatesDiv
      ref={compRef}
      className={`${
        isSearchPage && !isMobileView
          ? 'search-dates-wrapper'
          : isSearchPage && isMobileView
          ? 'search-mobile-dates-wrapper'
          : ''
      }
      ${enableTwoSeparateButtonsForDates ? 'shop-page-split-dates-box-wrapper' : ''}
       dates-wrapper`}
    >
      {!isMobileView && (
        <>
          {/* To determine if single date or double date field to display for ADF and Search Pages */}
          {(isSearchPage && enableTwoSeparateButtonsForDates) || isADFPage ? (
            tabSelected === tab2Label ? (
              <ShopPageSingleDateHeaderBox
                focusedCheckIn={focusedState === START_DATE}
                onClickCheckIn={(): void => {
                  showPopup();
                  setFocusedState(START_DATE);
                }}
                checkInMonth={getLocalizedDate(fromDate)}
                numberOfNights={nightCount}
                yearSelected={checkLocaleToAddYears(currentLocale, yearSelected)}
                monthSelected={monthSelected + 1}
                backHandler={handleBackBtn}
                isADFPage={isADFPage}
                currentLocale={currentLocale}
              />
            ) : (
              <ShopPageDatesHeaderBox
                checkInDate={getLocalizedDate(fromDate)}
                checkOutDate={getLocalizedDate(toDate)}
                focusedCheckIn={focusedState === START_DATE}
                onClickCheckIn={(): void => {
                  showPopup();
                  setFocusedState(START_DATE);
                }}
                onClickCheckOut={(): void => {
                  showPopup();
                  setFocusedState(END_DATE);
                }}
                isADFPage={isADFPage}
              />
            )
          ) : (
            <button type="button" onClick={showPopup} aria-expanded={popupOpenState} className="hover-60">
              <span className="search__calendar-label icon-nav---book t-label-xs">{labelText}</span>
              <span className={`h-r-form-field-txt search__calendar-value${!fromDate && !toDate ? '-empty' : ''}`}>
                {displayText}
              </span>
            </button>
          )}
        </>
      )}
      {isMobileView || popupOpenState ? (
        <Popup
          show={true}
          handleBlur={!isMobileView}
          popupOpenState={popupOpenState}
          setPopupOpenState={setPopupOpenState}
          className={`standard search-dates-popup ${
            isSearchPage && !isMobileView && 'search-page-dates-popup custom-search-popup'
          } ${enableFlexibleSearch === 'true' && ' search-page-dates-flexible'} ${
            document.body.style.overflow === 'hidden' && 'flexible-active custom-scrollbar'
          }
          ${isADFPage && 'shop-adf-dates-wrapper'}
          `}
        >
          {isMobileView ? (
            enableTwoSeparateButtonsForDates ? (
              <PopupHeader
                labelIcon={undefined}
                labelText=""
                mainText=""
                cancelLabel={cancel}
                cancelClickHandler={isMobileView && onCancel && !flexibleSearchTab ? onCancel : undefined}
                className="search-dates-popup__header"
              >
                {/* To show the single date field for ADF and Search Pages */}

                {(isADFPage || isSearchPage) && tabSelected === tab2Label ? (
                  <ShopPageSingleDateHeaderBox
                    focusedCheckIn={focusedState === START_DATE}
                    onClickCheckIn={(): void => {
                      showPopup();
                      setFocusedState(START_DATE);
                    }}
                    checkInMonth={getLocalizedDate(fromDate, alternateDateFormat)}
                    numberOfNights={nightCount}
                    yearSelected={checkLocaleToAddYears(currentLocale, yearSelected)}
                    monthSelected={monthSelected + 1}
                    backHandler={handleBackBtn}
                    isADFPage={isADFPage}
                    currentLocale={currentLocale}
                  />
                ) : (
                  <ShopPageDatesHeaderBox
                    checkInDate={getLocalizedDate(fromDate, alternateDateFormat)}
                    checkOutDate={getLocalizedDate(toDate, alternateDateFormat)}
                    focusedCheckIn={focusedState === START_DATE}
                    onClickCheckIn={(): void => {
                      showPopup();
                      setFocusedState(START_DATE);
                    }}
                    onClickCheckOut={(): void => {
                      showPopup();
                      setFocusedState(END_DATE);
                    }}
                    isADFPage={isADFPage}
                  />
                )}
              </PopupHeader>
            ) : (
              <PopupHeader
                labelIcon={'icon-nav---book'}
                labelText={labelText}
                mainText={displayText}
                cancelLabel={cancel}
                cancelClickHandler={isMobileView && onCancel ? onCancel : undefined}
                className="search-dates-popup__header"
              />
            )
          ) : (
            <>
              {/* To show the  date field based on conditions when popup is open for ADF and Search Pages */}

              {(isSearchPage || isADFPage) && tabSelected === tab2Label ? (
                <SingleDateHeader
                  checkInDate={getLocalizedDate(fromDate, alternateDateFormat)}
                  yearSelected={yearSelected}
                  monthSelected={monthSelected + 1}
                  onClearCheckIn={handleResetCalendar}
                  focusedCheckIn={focusedState === START_DATE}
                  onClickCheckIn={(): void => setFocusedState(START_DATE)}
                  showIcon={showIcon}
                />
              ) : (
                <DatesHeader
                  checkInDate={getLocalizedDate(fromDate, alternateDateFormat)}
                  checkOutDate={getLocalizedDate(toDate, alternateDateFormat)}
                  onClearCheckIn={handleClearCheckIn}
                  onClearCheckOut={handleClearCheckOut}
                  focusedCheckIn={focusedState === START_DATE}
                  onClickCheckIn={(): void => handleCheckinClicked()}
                  onClickCheckOut={(): void => setFocusedState(END_DATE)}
                />
              )}
            </>
          )}
          {isSearchPage && enableFlexibleSearch === 'true' && (
            <span className="toggle-container">
              <div className="toggle-container__seperator"></div>
              <ToggleSwitch
                tab1Label={tab1Label}
                tab2Label={tab2Label}
                activeTab={tabSelected}
                clickHandler={handleTabClick}
              />
            </span>
          )}

          <PopupMain
            className={`search-dates-popup__main ${
              variation !== VARIATION_DESKTOP && !flexible ? 'h-r-center-item' : ''
            }`}
          >
            <ErrorBoundary>
              <Suspense fallback={''}>
                {/* <div className="h-r-center-item"> */}
                {/* To show the flexible or normal calendar for ADF and Search Pages */}

                {(isSearchPage || isADFPage) && tabSelected === tab2Label ? (
                  <FlexibleDateCalendar
                    monthList={monthsValue}
                    setDateHandler={updateDatesOnFlexiSelection}
                    maxAllowedDays={CALENDAR_MAX_ALLOWED_DAYS}
                    singleDateLimit={singleDateLimit}
                    useEnglishMomentForCalculation={useEnglishMomentForCalculation}
                  />
                ) : (
                  <Calendar
                    startDate={fromDate ?? null}
                    endDate={toDate ?? null}
                    focusedInput={focusedState}
                    setDateHandler={setDateHandler}
                    setFocusedInputHandler={(newState: FocusedInputType): void => setFocusedState(newState)}
                    variation={
                      variation === VARIATION_TABLET && isSearchPage && !isMobileView ? VARIATION_DESKTOP : variation
                    }
                    isThereTwoDateInputBox={enableTwoSeparateButtonsForDates}
                    nightLabel={night}
                    nightsLabel={nights}
                    maxAllowedDays={
                      isMobileView ? moment(singleDateLimit).diff(moment(), 'd') : CALENDAR_MAX_ALLOWED_DAYS
                    }
                    weekdays={shortWeekdays}
                    months={monthsValue}
                    weekdaysLong={weekdaysLongName}
                    monthsShort={monthsShortValue}
                    weekdaysShort={weekDayInitial}
                    firstDayOfWeek={firstDayOfWeek as string}
                    disableHover={disableHover}
                    fromSearchForm={fromSearchForm}
                    locale={currentLocale}
                    yearLabel={isADFPage ? yearSearchLabel : yearLabel}
                    isAdfPage={isADFPage}
                    customToDate={new Date(singleDateLimit)}
                  />
                )}
                {/* </div> */}
              </Suspense>
            </ErrorBoundary>
            {showFlexible && isMobileView ? <FlexibleDate onChange={flexibleChangeHandler} checked={!!flexible} /> : ''}
          </PopupMain>
          {isMobileView ? (
            flexibleSearchTab ? (
              <PopupFooter
                primaryBtnLabel={done}
                primaryBtnClickHandler={changeMobileStateOnDone}
                resetLabel={clear}
                resetClickHandler={handleResetCalendar}
                isSearchPage={isSearchPage}
              />
            ) : (
              <PopupFooter primaryBtnLabel={done} primaryBtnClickHandler={changeMobileStateOnDone} />
            )
          ) : (
            <StyledDatesFooter>
              {showFlexible ? <FlexibleDate onChange={flexibleChangeHandler} checked={!!flexible} /> : ''}
              {isSearchPage ? (
                tabSelected === tab2Label ? (
                  <button
                    type="button"
                    className={`custom_click_track m-nav-link reset-btn ${
                      monthSelected < 0 ? 'hide-reset-btn' : 'show-reset-btn'
                    }`}
                    {...{
                      custom_click_track_value: `${clickTrackingLoc}|  Date Picker ${'Reset Calendar'} button |internal`,
                    }}
                    onClick={handleResetCalendar}
                  >
                    {reset}
                  </button>
                ) : (
                  <div />
                )
              ) : (
                ''
              )}

              <button
                type="button"
                onClick={closePopup}
                className={`m-button-s  custom_click_track search-page-done-button ${
                  enableFlexibleSearch === 'true' && ' search-page-flexible-done'
                }`}
                {...{ custom_click_track_value: `${clickTrackingLoc}| Date Picker Done button click |internal` }}
              >
                {done}
              </button>
            </StyledDatesFooter>
          )}
        </Popup>
      ) : (
        ''
      )}
    </StyledAddDatesDiv>
  );
};

export default AddDates;
